import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import Box from "@mui/material/Box";

import { isViewRecipeOpenSelector, setIsViewRecipeOpen } from "../../app/recipePage/recipePage.slice";
import { useAppDispatch, useAppSelector } from "../../app/store/hooks";
import InsideIngredients from "../viewRecipe/insideIngredients";
import CreateSubstep from "./recipeSteps/createSubstep";
import OutsideInstruction from "./recipeSteps/outsideInstruction";
import TimerInstruction from "./recipeSteps/timerInstruction";
import styles from './stepView.module.css';
import { CREATED_SOURCE, SubStepTypeEnum } from "./utils/constants";

const progressBarClasses = (userCreated: boolean, index: number, substepsIndex: number, islastStep: boolean) => {
    const initialProgressBarWithoutStep = userCreated && (index === 0);
    const lastSubstepProgressBar = (index === substepsIndex) && islastStep;

    if (lastSubstepProgressBar) {
        return `${styles.progress_bar} ${styles.last_step_progress_bar}`;
    }

    return `${styles.progress_bar} ${styles.mid_step_progress_bar}`;
};

const StepsView = ({ stepId, stepIndex, stepAdded, substeps, islastStep, createdSource, createdBy, recipeId, setIsStepAdded }: { stepId: string | null, stepIndex: number, stepAdded?: boolean, substeps: Array<any>, islastStep: boolean, createdSource?: string, createdBy?: string, recipeId: string | undefined, setIsStepAdded?: React.Dispatch<React.SetStateAction<boolean>> }) => {
    const user = useAppSelector((state) => state.main.user);
    const createSubstepOpen = useAppSelector((state) => state.recipeCreation.createSubstepOpen);
    const selectedStepId = useAppSelector((state) => state.recipeCreation.stepId);
    const selectedSubstepId = useAppSelector((state) => state.recipePage.substepEdit.substepId);
    const selectedSubstepUId = useAppSelector((state) => state.recipePage.substepEdit.subStepUId);
    const userCreated = createdSource === CREATED_SOURCE.USER_CREATED;
    const recipeCreatedByUser = createdBy === user?.id;
    const userCreatedRecipe = userCreated && recipeCreatedByUser;
    const substepsIndex = substeps.length - 1;
    const history = useHistory();
    const isViewRecipeOpen = useAppSelector(isViewRecipeOpenSelector)
    const dispatch = useAppDispatch();

    useEffect(() => {
        const handlePop = () => {
            if (history.action === 'POP' && isViewRecipeOpen) {
                dispatch(setIsViewRecipeOpen(false));
            }
        };

        window.addEventListener('popstate', handlePop);

        return () => {
            window.removeEventListener('popstate', handlePop);
        };
    }, []);

    if (createSubstepOpen && (selectedStepId === stepId)) {
        return <CreateSubstep setIsStepAdded={setIsStepAdded} stepAdded={stepAdded} stepId={stepId}
            substepId={selectedSubstepId} subStepUId={selectedSubstepUId} substeps={substeps} />;
    }

    return (
        <Box className={styles.steps_view_container}>
            {substeps?.map((substep: any, index: number) => (
                <div key={substep.subStepUId}>
                    <Box className={styles.substep_container}>
                        <Box className={progressBarClasses(userCreated, index, substepsIndex, islastStep)} />
                        {substep?.type === SubStepTypeEnum.OUTSIDE_INSTRUCTION && (
                            <OutsideInstruction
                                stepId={stepId}
                                stepIndex={stepIndex}
                                substep={substep}
                                substepIndex={index}
                                userCreated={userCreated}
                                userCreatedRecipe={userCreatedRecipe}
                            />
                        )}
                        {substep?.type === SubStepTypeEnum.INSTRUCTION && (
                            <TimerInstruction
                                stepId={stepId}
                                stepIndex={stepIndex}
                                substep={substep}
                                substepIndex={index}
                                userCreatedRecipe={userCreatedRecipe}
                            />
                        )}
                        {substep?.type === (SubStepTypeEnum.INGREDIENTS || SubStepTypeEnum.CUSTOM_INGREDIENT) && (
                            <InsideIngredients
                                recipeId={recipeId}
                                stepId={stepId}
                                stepIndex={stepIndex}
                                substep={substep}
                                substepIndex={index}
                                userCreatedRecipe={userCreatedRecipe}
                            />
                        )}
                    </Box>
                </div>
            ))}
        </Box>
    );
};

export default StepsView;
